import React, {
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import { motion } from "framer-motion";
import { MdArrowOutward } from "react-icons/md";
import officialLogo from "../../assets/logo/the-first-noncarbon-artist.png";
import Carousel from "../Carousel/Carousel";
import "./CardCurtainReveal.css";

const curtainVariants = {
  visible: {
    clipPath: "polygon(0 0, 100% 0, 100% 100%, 0 100%)",
    transition: {
      duration: 0.4,
      ease: [0.25, 1.5, 0.5, 1],
    },
  },
  hidden: {
    clipPath: "polygon(50% 0, 50% 0, 50% 100%, 50% 100%)",
    transition: {
      duration: 0.3,
      ease: [0.25, 1.5, 0.5, 1],
    },
  },
};

const CardCurtainRevealContext = createContext(undefined);

const useCardCurtainRevealContext = () => {
  const context = useContext(CardCurtainRevealContext);

  if (!context) {
    throw new Error(
      "useCardCurtainRevealContext must be used within CardCurtainReveal",
    );
  }

  return context;
};

const joinClasses = (...classes) => classes.filter(Boolean).join(" ");

export const CardCurtainReveal = forwardRef(
  ({ children, className = "", ...props }, ref) => {
    const [isMouseIn, setIsMouseIn] = useState(false);
    const handleMouseEnter = useCallback(() => setIsMouseIn(true), []);
    const handleMouseLeave = useCallback(() => setIsMouseIn(false), []);

    return (
      <CardCurtainRevealContext.Provider value={{ isMouseIn }}>
        <div
          ref={ref}
          className={joinClasses("card-curtain-reveal", className)}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          {...props}
        >
          {children}
        </div>
      </CardCurtainRevealContext.Provider>
    );
  },
);

CardCurtainReveal.displayName = "CardCurtainReveal";

export const CardCurtainRevealFooter = forwardRef(
  ({ className = "", children, ...props }, ref) => {
    const { isMouseIn } = useCardCurtainRevealContext();

    return (
      <motion.div
        ref={ref}
        className={joinClasses("card-curtain-reveal__footer", className)}
        variants={curtainVariants}
        animate={isMouseIn ? "visible" : "hidden"}
        {...props}
      >
        {children}
      </motion.div>
    );
  },
);

CardCurtainRevealFooter.displayName = "CardCurtainRevealFooter";

export const CardCurtainRevealBody = forwardRef(
  ({ className = "", ...props }, ref) => (
    <div
      ref={ref}
      className={joinClasses("card-curtain-reveal__body", className)}
      {...props}
    />
  ),
);

CardCurtainRevealBody.displayName = "CardCurtainRevealBody";

export const CardCurtainRevealTitle = forwardRef(
  ({ className = "", ...props }, ref) => {
    const { isMouseIn } = useCardCurtainRevealContext();

    return (
      <motion.h2
        ref={ref}
        className={joinClasses("card-curtain-reveal__title", className)}
        animate={isMouseIn ? { y: 0 } : { y: 170 }}
        transition={{ duration: 0.3, ease: "easeOut" }}
        {...props}
      />
    );
  },
);

CardCurtainRevealTitle.displayName = "CardCurtainRevealTitle";

export const CardCurtain = forwardRef(
  ({ className = "", ...props }, ref) => {
    const { isMouseIn } = useCardCurtainRevealContext();

    return (
      <motion.div
        ref={ref}
        className={joinClasses("card-curtain-reveal__curtain", className)}
        variants={curtainVariants}
        animate={isMouseIn ? "visible" : "hidden"}
        {...props}
      />
    );
  },
);

CardCurtain.displayName = "CardCurtain";

export const CardCurtainRevealDescription = forwardRef(
  ({ className = "", children, ...props }, ref) => {
    const { isMouseIn } = useCardCurtainRevealContext();

    return (
      <motion.div
        ref={ref}
        className={joinClasses("card-curtain-reveal__description", className)}
        variants={curtainVariants}
        animate={isMouseIn ? "visible" : "hidden"}
        {...props}
      >
        {children}
      </motion.div>
    );
  },
);

CardCurtainRevealDescription.displayName = "CardCurtainRevealDescription";

const toAbsoluteUrl = (url) => {
  if (!url) return null;
  if (url.startsWith("http://") || url.startsWith("https://")) return url;

  const apiBaseUrl = process.env.REACT_APP_API_URL || "http://localhost:8888";

  if (url.startsWith("/")) {
    return `${apiBaseUrl}${url}`;
  }

  return `${apiBaseUrl}/${url}`;
};

export const LatestNewsCurtainCard = ({ item }) => {
  const cardRef = useRef(null);
  const [isMouseIn, setIsMouseIn] = useState(false);
  const images = useMemo(() => {
    const imageList = [];

    if (item?.images && Array.isArray(item.images)) {
      imageList.push(...item.images.filter(Boolean));
    } else if (item?.image) {
      if (Array.isArray(item.image)) {
        imageList.push(...item.image.filter(Boolean));
      } else {
        imageList.push(item.image);
      }
    }

    return imageList.map((url) => toAbsoluteUrl(url)).filter(Boolean);
  }, [item]);

  const imageUrl = images[0] || null;
  const hasMultipleImages = images.length > 1;
  const carouselItems = useMemo(
    () =>
      images.map((img, index) => ({
        id: `${item?.title || "news"}-${index}`,
        url: img,
        type: "image",
      })),
    [images, item?.title],
  );
  const coverVariants = useMemo(
    () => ({
      closed: {
        x: 0,
        transition: {
          duration: 0.32,
          ease: [0.25, 1.5, 0.5, 1],
        },
      },
      open: {
        x: "-100%",
        transition: {
          duration: 0.4,
          ease: [0.25, 1.5, 0.5, 1],
        },
      },
    }),
    [],
  );
  const rightCoverVariants = useMemo(
    () => ({
      closed: {
        x: 0,
        transition: {
          duration: 0.32,
          ease: [0.25, 1.5, 0.5, 1],
        },
      },
      open: {
        x: "100%",
        transition: {
          duration: 0.4,
          ease: [0.25, 1.5, 0.5, 1],
        },
      },
    }),
    [],
  );

  const handleClick = useCallback(() => {
    if (item?.url) {
      window.open(item.url, "_blank", "noopener,noreferrer");
    }
  }, [item]);

  const handleOpenLink = useCallback((event) => {
    event.stopPropagation();
  }, []);

  const handlePointerMove = useCallback(
    (event) => {
      if (!cardRef.current) return;

      const card = cardRef.current;
      const rect = card.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;
      const centerX = rect.width / 2;
      const centerY = rect.height / 2;
      const rotateX = (y - centerY) / 15;
      const rotateY = (centerX - x) / 14;

      card.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) translateY(-3px)`;
      setIsMouseIn(true);
    },
    [],
  );

  const resetPointerState = useCallback(() => {
    if (!cardRef.current) return;

    cardRef.current.style.transform =
      "perspective(1000px) rotateX(0deg) rotateY(0deg) translateY(0)";
    setIsMouseIn(false);
  }, []);

  if (!item) {
    return null;
  }

  return (
    <div
      ref={cardRef}
      className="thumbnail-container thumbnail-container--team-holographic latest-news-curtain-card"
      onClick={handleClick}
      onMouseEnter={() => setIsMouseIn(true)}
      onMouseMove={handlePointerMove}
      onMouseLeave={resetPointerState}
    >
      {hasMultipleImages ? (
        <div className="thumbnail-carousel-wrapper">
          <Carousel
            mediaItems={carouselItems}
            showIndicators={true}
            className="thumbnail-carousel"
          />
        </div>
      ) : (
        imageUrl && (
          <img
            src={imageUrl}
            alt={item.title || "Latest news"}
            className="thumbnail-image"
          />
        )
      )}

      <div className="thumbnail-content">
        <div className="thumbnail-site-name">{item.site_name}</div>
        <h3 className="thumbnail-title">{item.title}</h3>
        <p className="thumbnail-description">{item.description}</p>
      </div>

      <motion.div
        className="latest-news-curtain-card__cover"
      >
        <motion.div
          className="latest-news-curtain-card__curtain-panel latest-news-curtain-card__curtain-panel--left"
          variants={coverVariants}
          animate={isMouseIn ? "open" : "closed"}
        />
        <motion.div
          className="latest-news-curtain-card__curtain-panel latest-news-curtain-card__curtain-panel--right"
          variants={rightCoverVariants}
          animate={isMouseIn ? "open" : "closed"}
        />

        <motion.div
          className="latest-news-curtain-card__cover-body"
          animate={
            isMouseIn
              ? { opacity: 0, y: 28 }
              : { opacity: 1, y: 0 }
          }
          transition={{ duration: 0.22, ease: "easeOut" }}
        >
          <div className="latest-news-curtain-card__logo-row">
            <div className="latest-news-curtain-card__logo-shell">
              <img
                src={officialLogo}
                alt="The First NonCarbon Artist"
                className="latest-news-curtain-card__logo"
              />
            </div>
            <span className="latest-news-curtain-card__label">Latest News</span>
          </div>

          <motion.h2
            className="latest-news-curtain-card__headline"
          >
            Latest News
            <br />
            Behind the Curtain
          </motion.h2>

          {item.url && (
            <a
              href={item.url}
              target="_blank"
              rel="noopener noreferrer"
              className="latest-news-curtain-card__button"
              aria-label="Open latest news"
              onClick={handleOpenLink}
            >
              <MdArrowOutward aria-hidden="true" />
            </a>
          )}
        </motion.div>
      </motion.div>
    </div>
  );
};

export default LatestNewsCurtainCard;
